import {
  ActionBar,
  ActionButton,
  ActionInput,
  ActionTextarea,
  Alert,
  AlertDescription,
  AlertTitle,
  AppBreadcrumb,
  Badge,
  BrandLogo,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardListSkeleton,
  CardTitle,
  ChartSkeleton,
  DataTableSkeleton,
  EmptyState,
  InlineSkeleton,
  Input,
  Label,
  LayoutHeader,
  ListSkeleton,
  MiniSparkline,
  Numeric,
  PageTitle,
  PanelToggleButton,
  PersistBadge,
  PersistStats,
  Progress,
  ScoreBar,
  SectionCard,
  SectionCardSkeleton,
  Separator,
  Skeleton,
  StatCard,
  StatCardSkeleton,
  Textarea,
  TrendIndicator,
  cn,
  formatNumber,
  formatScore,
  usePersistentState
} from "./chunk-6PEP2SRX.js";

// src/dialog.tsx
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { jsx, jsxs } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-surface-overlay/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [
  /* @__PURE__ */ jsx(DialogOverlay, {}),
  /* @__PURE__ */ jsx(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-sm border border-stroke-default bg-surface-base p-6 shadow-pop duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      ),
      ...props,
      children
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
function DialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        className
      ),
      ...props
    }
  );
}
function DialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      ),
      ...props
    }
  );
}
var DialogTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-text-tertiary", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/alert-dialog.tsx
import * as React2 from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AlertDialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-surface-overlay/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
var AlertDialogContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs2(AlertDialogPortal, { children: [
  /* @__PURE__ */ jsx2(AlertDialogOverlay, {}),
  /* @__PURE__ */ jsx2(
    AlertDialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-sm border border-stroke-default bg-surface-base p-6 shadow-pop duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      ),
      ...props
    }
  )
] }));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
function AlertDialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        className
      ),
      ...props
    }
  );
}
function AlertDialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      ),
      ...props
    }
  );
}
var AlertDialogTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
var AlertDialogDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-text-tertiary", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
var AlertDialogAction = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(
      "inline-flex h-9 items-center justify-center rounded-sm bg-signal-danger px-4 text-sm font-medium text-text-inverse transition-colors hover:bg-signal-danger/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-focus disabled:pointer-events-none disabled:opacity-50",
      className
    ),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
var AlertDialogCancel = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      "inline-flex h-9 items-center justify-center rounded-sm border border-stroke-default bg-surface-base px-4 text-sm font-medium transition-colors hover:bg-accent-soft hover:text-text-brand focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-focus disabled:pointer-events-none disabled:opacity-50",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

// src/tabs.tsx
import * as React3 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx3 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-9 items-center justify-center rounded-sm bg-surface-sunken p-1 text-text-tertiary",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-[12px] font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-surface-base data-[state=active]:text-text-primary data-[state=active]:shadow-none",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-focus focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/select.tsx
import * as React4 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React4.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs3(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-9 w-full items-center justify-between rounded-sm border border-stroke-default bg-surface-base px-3 py-2 text-[13px] ring-offset-background placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-stroke-focus focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx4(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          className: "opacity-50",
          children: /* @__PURE__ */ jsx4("path", { d: "m6 9 6 6 6-6" })
        }
      ) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectContent = React4.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx4(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx4(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-sm border border-stroke-default bg-surface-raised text-text-primary shadow-pop data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: /* @__PURE__ */ jsx4(
      SelectPrimitive.Viewport,
      {
        className: cn(
          "p-1",
          position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        ),
        children
      }
    )
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("py-1.5 pl-8 pr-2 text-[12px] font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React4.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs3(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-[13px] outline-none focus:bg-accent-soft focus:text-text-brand data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx4("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx4(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: /* @__PURE__ */ jsx4("path", { d: "M20 6 9 17l-5-5" })
        }
      ) }) }),
      /* @__PURE__ */ jsx4(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-surface-sunken", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/popover.tsx
import * as React5 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx5 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverContent = React5.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx5(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx5(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-sm border border-stroke-default bg-surface-raised p-4 text-text-primary shadow-pop outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/tooltip.tsx
import * as React6 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx6 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React6.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx6(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx6(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-sm bg-accent px-3 py-1.5 text-xs text-text-inverse animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/dropdown-menu.tsx
import * as React7 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuSubTrigger = React7.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-[13px] outline-none focus:bg-accent-soft data-[state=open]:bg-accent-soft",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx7(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          className: "ml-auto",
          children: /* @__PURE__ */ jsx7("path", { d: "m9 18 6-6-6-6" })
        }
      )
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-sm border border-stroke-default bg-surface-raised p-1 text-text-primary shadow-pop data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React7.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx7(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx7(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-sm border border-stroke-default bg-surface-raised p-1 text-text-primary shadow-pop data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React7.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx7(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-[13px] outline-none transition-colors focus:bg-accent-soft focus:text-text-brand data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React7.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs4(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-[13px] outline-none transition-colors focus:bg-accent-soft focus:text-text-brand data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx7("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx7(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx7(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: /* @__PURE__ */ jsx7("path", { d: "M20 6 9 17l-5-5" })
        }
      ) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React7.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-[13px] outline-none transition-colors focus:bg-accent-soft focus:text-text-brand data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx7("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx7(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx7(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "12",
          height: "12",
          viewBox: "0 0 24 24",
          fill: "currentColor",
          children: /* @__PURE__ */ jsx7("circle", { cx: "12", cy: "12", r: "6" })
        }
      ) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React7.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx7(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-[12px] font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-surface-sunken", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx7(
    "span",
    {
      className: cn(
        "ml-auto text-[11px] tracking-widest opacity-60",
        className
      ),
      ...props
    }
  );
}

// src/checkbox.tsx
import * as React8 from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { jsx as jsx8 } from "react/jsx-runtime";
var Checkbox = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  CheckboxPrimitive.Root,
  {
    ref,
    className: cn(
      "peer size-4 shrink-0 rounded-sm border border-accent shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-focus disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-accent data-[state=checked]:text-text-inverse",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx8(
      CheckboxPrimitive.Indicator,
      {
        className: cn("flex items-center justify-center text-current"),
        children: /* @__PURE__ */ jsx8(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            width: "16",
            height: "16",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "3",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            children: /* @__PURE__ */ jsx8("path", { d: "M20 6 9 17l-5-5" })
          }
        )
      }
    )
  }
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

// src/switch.tsx
import * as React9 from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { jsx as jsx9 } from "react/jsx-runtime";
var Switch = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  SwitchPrimitive.Root,
  {
    ref,
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-accent data-[state=unchecked]:bg-surface-input",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx9(
      SwitchPrimitive.Thumb,
      {
        className: cn(
          "pointer-events-none block size-4 rounded-full bg-surface-base shadow-pop ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitive.Root.displayName;

// src/radio-group.tsx
import * as React10 from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { jsx as jsx10 } from "react/jsx-runtime";
var RadioGroup2 = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
  RadioGroupPrimitive.Root,
  {
    ref,
    className: cn("grid gap-2", className),
    ...props
  }
));
RadioGroup2.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
  RadioGroupPrimitive.Item,
  {
    ref,
    className: cn(
      "aspect-square size-4 rounded-full border border-accent text-text-brand shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-stroke-focus disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx10(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx10(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        width: "10",
        height: "10",
        viewBox: "0 0 24 24",
        fill: "currentColor",
        children: /* @__PURE__ */ jsx10("circle", { cx: "12", cy: "12", r: "6" })
      }
    ) })
  }
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// src/sheet.tsx
import * as React11 from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var Sheet = SheetPrimitive.Root;
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SheetPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-surface-overlay/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetSideVariants = {
  top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
  bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
  left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
  right: "inset-y-0 right-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
};
var SheetContent = React11.forwardRef(({ side = "right", className, children, ...props }, ref) => /* @__PURE__ */ jsxs5(SheetPortal, { children: [
  /* @__PURE__ */ jsx11(SheetOverlay, {}),
  /* @__PURE__ */ jsx11(
    SheetPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed z-50 gap-4 bg-surface-base p-6 shadow-pop transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
        sheetSideVariants[side],
        className
      ),
      ...props,
      children
    }
  )
] }));
SheetContent.displayName = SheetPrimitive.Content.displayName;
function SheetHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx11(
    "div",
    {
      className: cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        className
      ),
      ...props
    }
  );
}
function SheetFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx11(
    "div",
    {
      className: cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      ),
      ...props
    }
  );
}
var SheetTitle = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SheetPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold text-text-primary", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  SheetPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-text-tertiary", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/scroll-area.tsx
import * as React12 from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var ScrollArea = React12.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs6(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx12(ScrollAreaPrimitive.Viewport, { className: "h-full max-h-[inherit] w-full rounded-[inherit]", children }),
      /* @__PURE__ */ jsx12(ScrollBar, {}),
      /* @__PURE__ */ jsx12(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React12.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx12(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx12(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-stroke-default" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/accordion.tsx
import * as React13 from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx13(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs7(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx13(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          className: "size-4 shrink-0 text-text-tertiary transition-transform duration-200",
          children: /* @__PURE__ */ jsx13("path", { d: "m6 9 6 6 6-6" })
        }
      )
    ]
  }
) }));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx13(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props,
    children: /* @__PURE__ */ jsx13("div", { className: cn("pb-4 pt-0", className), children })
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// src/sonner.tsx
import { Toaster as SonnerToaster, toast } from "sonner";
import { jsx as jsx14 } from "react/jsx-runtime";
function Toaster(props) {
  return /* @__PURE__ */ jsx14(
    SonnerToaster,
    {
      className: "toaster group",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-surface-base group-[.toaster]:text-text-primary group-[.toaster]:border-stroke-default group-[.toaster]:shadow-pop",
          description: "group-[.toast]:text-text-tertiary",
          actionButton: "group-[.toast]:bg-accent group-[.toast]:text-text-inverse",
          cancelButton: "group-[.toast]:bg-surface-sunken group-[.toast]:text-text-tertiary"
        }
      },
      ...props
    }
  );
}

// src/command.tsx
import * as React14 from "react";
import { Command as CommandPrimitive } from "cmdk";
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var Command = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive,
  {
    ref,
    className: cn(
      "flex h-full w-full flex-col overflow-hidden rounded-sm bg-surface-raised text-text-primary",
      className
    ),
    ...props
  }
));
Command.displayName = CommandPrimitive.displayName;
function CommandDialog({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx15(Dialog, { ...props, children: /* @__PURE__ */ jsx15(DialogContent, { className: "overflow-hidden p-0", children: /* @__PURE__ */ jsx15(Command, { className: "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-tertiary [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:size-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:size-5", children }) }) });
}
var CommandInput = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs8("div", { className: "flex items-center border-b px-3", "cmdk-input-wrapper": "", children: [
  /* @__PURE__ */ jsxs8(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: "mr-2 size-4 shrink-0 opacity-50",
      children: [
        /* @__PURE__ */ jsx15("circle", { cx: "11", cy: "11", r: "8" }),
        /* @__PURE__ */ jsx15("path", { d: "m21 21-4.3-4.3" })
      ]
    }
  ),
  /* @__PURE__ */ jsx15(
    CommandPrimitive.Input,
    {
      ref,
      className: cn(
        "flex h-10 w-full rounded-sm bg-transparent py-3 text-sm outline-none placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  )
] }));
CommandInput.displayName = CommandPrimitive.Input.displayName;
var CommandList = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive.List,
  {
    ref,
    className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className),
    ...props
  }
));
CommandList.displayName = CommandPrimitive.List.displayName;
var CommandEmpty = React14.forwardRef((props, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive.Empty,
  {
    ref,
    className: "py-6 text-center text-sm",
    ...props
  }
));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
var CommandGroup = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive.Group,
  {
    ref,
    className: cn(
      "overflow-hidden p-1 text-text-primary [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-tertiary",
      className
    ),
    ...props
  }
));
CommandGroup.displayName = CommandPrimitive.Group.displayName;
var CommandSeparator = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 h-px bg-stroke-default", className),
    ...props
  }
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
var CommandItem = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(
  CommandPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent-soft data-[selected=true]:text-text-brand data-[disabled=true]:opacity-50",
      className
    ),
    ...props
  }
));
CommandItem.displayName = CommandPrimitive.Item.displayName;
function CommandShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx15(
    "span",
    {
      className: cn(
        "ml-auto text-xs tracking-widest text-text-tertiary",
        className
      ),
      ...props
    }
  );
}

// src/calendar.tsx
import { DayPicker } from "react-day-picker";
import { jsx as jsx16 } from "react/jsx-runtime";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx16(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        button_previous: "absolute left-1 inline-flex size-7 items-center justify-center rounded-sm border border-stroke-default bg-transparent p-0 opacity-50 hover:opacity-100",
        button_next: "absolute right-1 inline-flex size-7 items-center justify-center rounded-sm border border-stroke-default bg-transparent p-0 opacity-50 hover:opacity-100",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-text-tertiary rounded-sm w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent-soft [&:has([aria-selected].day-outside)]:bg-accent-soft/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
        day_button: cn(
          "inline-flex size-8 items-center justify-center rounded-sm p-0 font-normal aria-selected:opacity-100",
          "hover:bg-accent-soft hover:text-text-brand",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-focus"
        ),
        range_end: "day-range-end",
        selected: "bg-accent text-text-inverse hover:bg-accent hover:text-text-inverse focus:bg-accent focus:text-text-inverse",
        today: "bg-accent-soft text-text-brand",
        outside: "day-outside text-text-tertiary aria-selected:bg-accent-soft/50 aria-selected:text-text-tertiary",
        disabled: "text-text-tertiary opacity-50",
        range_middle: "aria-selected:bg-accent-soft aria-selected:text-text-brand",
        hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation }) => /* @__PURE__ */ jsx16(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            width: "16",
            height: "16",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            className: "size-4",
            children: orientation === "left" ? /* @__PURE__ */ jsx16("path", { d: "m15 18-6-6 6-6" }) : /* @__PURE__ */ jsx16("path", { d: "m9 18 6-6-6 6" })
          }
        )
      },
      ...props
    }
  );
}
Calendar.displayName = "Calendar";

// src/sort-tabs.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function SortTabs({
  options,
  value,
  onChange,
  className
}) {
  return /* @__PURE__ */ jsx17(
    Tabs,
    {
      value,
      onValueChange: (v) => onChange(v),
      className,
      children: /* @__PURE__ */ jsx17(TabsList, { className: cn("gap-1.5 bg-transparent p-0"), children: options.map((opt) => /* @__PURE__ */ jsx17(
        TabsTrigger,
        {
          value: opt.key,
          className: "data-[state=active]:bg-accent data-[state=active]:text-text-inverse data-[state=active]:shadow-none data-[state=inactive]:bg-surface-sunken",
          children: opt.label
        },
        opt.key
      )) })
    }
  );
}

// src/theme/use-theme.ts
import { useSyncExternalStore } from "react";
var STORAGE_KEY = "datalab.theme";
var subscribers = /* @__PURE__ */ new Set();
var systemMql = null;
function ssrSnapshot() {
  return "light";
}
function currentTheme() {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}
function resolvePreference(pref) {
  if (pref === "system") {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return pref;
}
function subscribe(cb) {
  subscribers.add(cb);
  if (typeof window !== "undefined" && !systemMql) {
    systemMql = window.matchMedia("(prefers-color-scheme: dark)");
    systemMql.addEventListener("change", () => {
      const pref = readPreference();
      if (pref === "system") applyTheme("system");
    });
  }
  return () => {
    subscribers.delete(cb);
  };
}
function notify() {
  subscribers.forEach((cb) => cb());
}
function readPreference() {
  if (typeof localStorage === "undefined") return "system";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
    return "system";
  } catch {
    return "system";
  }
}
function applyTheme(pref) {
  if (typeof document === "undefined") return;
  const next = resolvePreference(pref);
  document.documentElement.classList.toggle("dark", next === "dark");
  notify();
}
function setTheme(pref) {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
  }
  applyTheme(pref);
}
function useTheme() {
  return useSyncExternalStore(subscribe, currentTheme, ssrSnapshot);
}
function getPreference() {
  return readPreference();
}

// src/density/density.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
function Density({ level, children, ...rest }) {
  return /* @__PURE__ */ jsx18("div", { "data-density": level, ...rest, children });
}

// src/tree-view.tsx
import * as React15 from "react";
import { jsx as jsx19 } from "react/jsx-runtime";
function flatten(nodes, expanded, depth = 0, out = []) {
  for (const node of nodes) {
    out.push({ node, depth });
    if (node.children && node.children.length > 0 && expanded.has(node.id)) {
      flatten(node.children, expanded, depth + 1, out);
    }
  }
  return out;
}
function useControlled(args) {
  const { controlled, defaultValue, onChange } = args;
  const isControlled = controlled !== void 0;
  const [internal, setInternal] = React15.useState(defaultValue);
  const value = isControlled ? controlled : internal;
  const setValue = React15.useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );
  return [value, setValue];
}
function TreeView({
  nodes,
  renderNode,
  selectedId,
  defaultSelectedId = null,
  onSelect,
  expandedIds,
  defaultExpandedIds,
  onExpandedChange,
  ariaLabel,
  className,
  scrollClassName
}) {
  const [selected, setSelected] = useControlled({
    controlled: selectedId,
    defaultValue: defaultSelectedId,
    onChange: (id) => {
      if (id != null) {
        const node = findNodeById(nodes, id);
        if (node) onSelect?.(id, node);
      }
    }
  });
  const stableControlledExpanded = React15.useMemo(
    () => expandedIds ? expandedIds : void 0,
    [expandedIds]
  );
  const [expanded, setExpanded] = useControlled({
    controlled: stableControlledExpanded,
    defaultValue: new Set(defaultExpandedIds ?? []),
    onChange: onExpandedChange
  });
  const [focusedId, setFocusedId] = React15.useState(null);
  const itemRefs = React15.useRef(/* @__PURE__ */ new Map());
  const visible = React15.useMemo(
    () => flatten(nodes, expanded),
    [nodes, expanded]
  );
  const effectiveFocusedId = focusedId && visible.some((v) => v.node.id === focusedId) ? focusedId : visible[0]?.node.id ?? null;
  const toggleExpand = React15.useCallback(
    (id) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpanded(next);
    },
    [expanded, setExpanded]
  );
  const focusItem = React15.useCallback((id) => {
    const el = itemRefs.current.get(id);
    if (el) el.focus();
  }, []);
  const handleKeyDown = React15.useCallback(
    (event, node, depth) => {
      const index = visible.findIndex((v) => v.node.id === node.id);
      if (index < 0) return;
      const hasChildren = !!(node.children && node.children.length > 0);
      const isExpanded = expanded.has(node.id);
      switch (event.key) {
        case "ArrowDown": {
          const next = visible[index + 1];
          if (next) {
            event.preventDefault();
            setFocusedId(next.node.id);
            focusItem(next.node.id);
          }
          break;
        }
        case "ArrowUp": {
          const prev = visible[index - 1];
          if (prev) {
            event.preventDefault();
            setFocusedId(prev.node.id);
            focusItem(prev.node.id);
          }
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          if (hasChildren && !isExpanded) {
            toggleExpand(node.id);
          } else if (hasChildren && isExpanded) {
            const next = visible[index + 1];
            if (next && next.depth > depth) {
              setFocusedId(next.node.id);
              focusItem(next.node.id);
            }
          }
          break;
        }
        case "ArrowLeft": {
          event.preventDefault();
          if (hasChildren && isExpanded) {
            toggleExpand(node.id);
          } else {
            for (let i = index - 1; i >= 0; i -= 1) {
              if (visible[i].depth < depth) {
                setFocusedId(visible[i].node.id);
                focusItem(visible[i].node.id);
                break;
              }
            }
          }
          break;
        }
        case "Home": {
          event.preventDefault();
          const first = visible[0];
          if (first) {
            setFocusedId(first.node.id);
            focusItem(first.node.id);
          }
          break;
        }
        case "End": {
          event.preventDefault();
          const last = visible[visible.length - 1];
          if (last) {
            setFocusedId(last.node.id);
            focusItem(last.node.id);
          }
          break;
        }
        case "Enter":
        case " ": {
          event.preventDefault();
          setSelected(node.id);
          break;
        }
        default:
          break;
      }
    },
    [visible, expanded, toggleExpand, focusItem, setSelected]
  );
  const tree = /* @__PURE__ */ jsx19(
    "ul",
    {
      role: "tree",
      "aria-label": ariaLabel,
      className: cn(
        "list-none space-y-0 p-0",
        // Radix ScrollArea renders its scrollbar absolutely over the viewport
        // (~12px wide). Reserve gutter on the list so rightmost content
        // (count badges, trailing icons) isn't occluded. Consumer className
        // still wins via tailwind-merge precedence.
        scrollClassName && "pr-3",
        className
      ),
      children: visible.map(({ node, depth }) => {
        const hasChildren = !!(node.children && node.children.length > 0);
        const isExpanded = hasChildren && expanded.has(node.id);
        const isSelected = selected === node.id;
        const isFocused = effectiveFocusedId === node.id;
        const nodeProps = {
          role: "treeitem",
          "aria-expanded": hasChildren ? isExpanded : void 0,
          "aria-selected": isSelected,
          "aria-level": depth + 1,
          tabIndex: isFocused ? 0 : -1,
          onKeyDown: (event) => handleKeyDown(event, node, depth),
          onFocus: () => setFocusedId(node.id),
          ref: (el) => {
            if (el) itemRefs.current.set(node.id, el);
            else itemRefs.current.delete(node.id);
          }
        };
        return /* @__PURE__ */ jsx19(React15.Fragment, { children: renderNode({
          node,
          depth,
          isExpanded,
          isSelected,
          isFocused,
          hasChildren,
          toggleExpand: () => toggleExpand(node.id),
          select: () => setSelected(node.id),
          nodeProps
        }) }, node.id);
      })
    }
  );
  if (scrollClassName) {
    return /* @__PURE__ */ jsx19(ScrollArea, { className: cn("w-full", scrollClassName), children: tree });
  }
  return tree;
}
function findNodeById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children && n.children.length > 0) {
      const found = findNodeById(n.children, id);
      if (found) return found;
    }
  }
  return null;
}
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ActionBar,
  ActionButton,
  ActionInput,
  ActionTextarea,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  AppBreadcrumb,
  Badge,
  BrandLogo,
  Button,
  Calendar,
  Card,
  CardContent,
  CardHeader,
  CardListSkeleton,
  CardTitle,
  ChartSkeleton,
  Checkbox,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  DataTableSkeleton,
  Density,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EmptyState,
  InlineSkeleton,
  Input,
  Label,
  LayoutHeader,
  ListSkeleton,
  MiniSparkline,
  Numeric,
  PageTitle,
  PanelToggleButton,
  PersistBadge,
  PersistStats,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Progress,
  RadioGroup2 as RadioGroup,
  RadioGroupItem,
  ScoreBar,
  ScrollArea,
  ScrollBar,
  SectionCard,
  SectionCardSkeleton,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  Skeleton,
  SortTabs,
  StatCard,
  StatCardSkeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TreeView,
  TrendIndicator,
  cn,
  formatNumber,
  formatScore,
  getPreference,
  setTheme,
  toast,
  usePersistentState,
  useTheme
};
//# sourceMappingURL=index.js.map